import { useState, useEffect, useMemo, useRef } from 'react';
import Marquee from 'react-fast-marquee';
import { useAnalyticsStream, useRecentEvents } from '@/contexts/AnalyticsStreamContext';
import { formatEventMessage } from '@/lib/EventContract';
import {
  Radio, Activity, MessageSquare, ThumbsUp, ThumbsDown,
  AlertTriangle, CheckCircle2, Users, Zap, TrendingUp,
  Volume2, VolumeX, Power, Clock, X
} from 'lucide-react';

const MAX_TICKER_EVENTS = 20;
/**
 * Play a notification sound for high-priority events
 * Uses Web Audio API - no permissions required
 */
const playAlertSound = (type = 'default') => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    const playTone = (frequency, startTime, duration, volume = 0.2) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };
    
    const now = audioContext.currentTime;
    
    if (type === 'gap') {
      // Alert sound for content gaps - attention-grabbing two-tone
      playTone(880, now, 0.15, 0.25);        // A5
      playTone(660, now + 0.15, 0.2, 0.2);   // E5 - descending alert
    } else if (type === 'negative') {
      // Softer alert for negative feedback
      playTone(440, now, 0.12, 0.15);        // A4
      playTone(349.23, now + 0.12, 0.15, 0.12); // F4
    } else {
      // Default subtle chime
      playTone(659.25, now, 0.1, 0.1);       // E5
    }
    
    setTimeout(() => audioContext.close(), 500);
  } catch (e) {
    console.log('Could not play alert sound:', e);
  }
};

/**
 * Live Broadcast Hero Component
 * 
 * A prominent, broadcast-style live activity feed for the Overview page.
 * Shows real-time events in a scrolling ticker format with a command center aesthetic.
 */
export function LiveBroadcastHero({ className = '' }) {
  const { events, isConnected, connectionStatus, stats, preferences, toggleSound, toggleEnabled } = useAnalyticsStream();
  const recentEvents = useRecentEvents(events, 60); // Last 60 minutes
  const lastEventCountRef = useRef(0);
  const processedEventIdsRef = useRef(new Set());
  const pendingEventsRef = useRef([]);
  const [tickerEvents, setTickerEvents] = useState([]);
  const [freshEventKeys, setFreshEventKeys] = useState([]);
  const freshTimeoutRef = useRef(null);

  // Use preferences from context
  const soundEnabled = preferences.soundEnabled;
  const streamEnabled = preferences.enabled;
  
  // Play sound for high-priority events
  useEffect(() => {
    if (!soundEnabled || recentEvents.length === 0) return;
    if (recentEvents.length <= lastEventCountRef.current) {
      lastEventCountRef.current = recentEvents.length;
      return;
    }
    
    // Get the newest event
    const latestEvent = recentEvents[recentEvents.length - 1];
    lastEventCountRef.current = recentEvents.length;
    
    // Play sound based on event type
    if (latestEvent.type === 'content_gap' && latestEvent.data?.is_new) {
      playAlertSound('gap');
    } else if (latestEvent.type === 'feedback' && latestEvent.data?.feedback === 'negative') {
      playAlertSound('negative');
    }
  }, [recentEvents, soundEnabled]);
  
  const getEventId = (event) => `${event.type}:${event.timestamp}:${JSON.stringify(event.data || {})}`;

  const flushPendingEvents = () => {
    if (pendingEventsRef.current.length === 0) {
      return;
    }

    const mergedEvents = [];
    setTickerEvents(prev => {
      const nextEvents = [...prev];
      while (pendingEventsRef.current.length > 0) {
        const mergedEvent = pendingEventsRef.current.shift();
        mergedEvents.push(mergedEvent);
        nextEvents.push(mergedEvent);
      }
      return nextEvents.slice(-MAX_TICKER_EVENTS);
    });

    if (mergedEvents.length > 0) {
      setFreshEventKeys(mergedEvents.map((event) => getEventId(event)));
      if (freshTimeoutRef.current) {
        clearTimeout(freshTimeoutRef.current);
      }
      freshTimeoutRef.current = setTimeout(() => {
        setFreshEventKeys([]);
        freshTimeoutRef.current = null;
      }, 700);
    }
  };

  useEffect(() => {
    if (recentEvents.length === 0) return;

    if (tickerEvents.length === 0 && processedEventIdsRef.current.size === 0) {
      const seeded = recentEvents.slice(-MAX_TICKER_EVENTS);
      seeded.forEach((event) => processedEventIdsRef.current.add(getEventId(event)));
      setTickerEvents(seeded);
      return;
    }

    const unseen = recentEvents.filter((event) => {
      const eventId = getEventId(event);
      if (processedEventIdsRef.current.has(eventId)) {
        return false;
      }
      processedEventIdsRef.current.add(eventId);
      return true;
    });

    if (unseen.length === 0) return;
    pendingEventsRef.current.push(...unseen);
  }, [recentEvents, tickerEvents.length]);

  useEffect(() => () => {
    if (freshTimeoutRef.current) {
      clearTimeout(freshTimeoutRef.current);
    }
  }, []);

  // Fallback flush so pending events don't stall if the marquee never
  // cycles (few items, paused on hover, tab hidden). flushPendingEvents
  // uses refs and functional setState, so the stale closure is intentional.
  useEffect(() => {
    const intervalId = setInterval(flushPendingEvents, 5000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayEvents = tickerEvents;
  
  const hasEvents = displayEvents.length > 0;

  const getBroadcastStatus = () => {
    if (!streamEnabled) {
      return {
        label: 'Paused',
        pulse: false,
        className: 'bg-muted text-muted-foreground border-border',
        dotClassName: 'bg-muted-foreground',
      };
    }

    if (isConnected) {
      return {
        label: 'Live',
        pulse: true,
        className: 'bg-red-500/10 dark:bg-red-500/20 border-red-500/20 dark:border-red-500/30 text-red-600 dark:text-red-400',
        dotClassName: 'bg-red-500',
      };
    }

    if (connectionStatus === 'connecting') {
      return {
        label: 'Connecting',
        pulse: true,
        className: 'bg-amber-500/10 dark:bg-amber-500/20 border-amber-500/20 dark:border-amber-500/30 text-amber-600 dark:text-amber-400',
        dotClassName: 'bg-amber-500',
      };
    }

    if (connectionStatus === 'reconnecting') {
      return {
        label: 'Reconnecting',
        pulse: true,
        className: 'bg-amber-500/10 dark:bg-amber-500/20 border-amber-500/20 dark:border-amber-500/30 text-amber-600 dark:text-amber-400',
        dotClassName: 'bg-amber-500',
      };
    }

    return {
      label: 'Offline',
      pulse: false,
      className: 'bg-muted text-muted-foreground border-border',
      dotClassName: 'bg-muted-foreground',
    };
  };

  const broadcastStatus = getBroadcastStatus();
  
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatRelativeFreshness = (timestamp) => {
    if (!timestamp) return '';
    const diffMs = Date.now() - new Date(timestamp).getTime();
    const diffSeconds = Math.max(0, Math.floor(diffMs / 1000));
    if (diffSeconds < 45) return 'just now';
    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };
  
  const getEventConfig = (event) => {
    const type = event.type;
    const data = event.data || {};
    
    switch (type) {
      // Chat events
      case 'retrieval':
      case 'query':
        return { icon: MessageSquare, color: 'text-blue-500 dark:text-blue-400', bg: 'bg-blue-500/10 dark:bg-blue-500/20', label: 'QUERY' };
      case 'session_started':
        return { icon: Zap, color: 'text-cyan-500 dark:text-cyan-400', bg: 'bg-cyan-500/10 dark:bg-cyan-500/20', label: 'SESSION' };
      case 'session_completed':
      case 'session_summary':
        return { icon: CheckCircle2, color: 'text-emerald-500 dark:text-emerald-400', bg: 'bg-emerald-500/10 dark:bg-emerald-500/20', label: 'COMPLETE' };
      
      // Feedback events
      case 'feedback':
      case 'thumbs_up':
        return { 
          icon: data.feedback === 'positive' ? ThumbsUp : ThumbsDown, 
          color: data.feedback === 'positive' ? 'text-emerald-500 dark:text-emerald-400' : 'text-rose-500 dark:text-rose-400', 
          bg: data.feedback === 'positive' ? 'bg-emerald-500/10 dark:bg-emerald-500/20' : 'bg-rose-500/10 dark:bg-rose-500/20', 
          label: 'FEEDBACK' 
        };
      case 'thumbs_down':
        return { icon: ThumbsDown, color: 'text-rose-500 dark:text-rose-400', bg: 'bg-rose-500/10 dark:bg-rose-500/20', label: 'FEEDBACK' };
      case 'comment_added':
        return { icon: MessageSquare, color: 'text-purple-500 dark:text-purple-400', bg: 'bg-purple-500/10 dark:bg-purple-500/20', label: 'COMMENT' };
      
      // Engagement events
      case 'bookmark':
        return { icon: Activity, color: 'text-amber-500 dark:text-amber-400', bg: 'bg-amber-500/10 dark:bg-amber-500/20', label: 'BOOKMARK' };
      case 'share':
        return { icon: Users, color: 'text-indigo-500 dark:text-indigo-400', bg: 'bg-indigo-500/10 dark:bg-indigo-500/20', label: 'SHARE' };
      case 'copy_response':
        return { icon: Activity, color: 'text-slate-500 dark:text-slate-400', bg: 'bg-slate-500/10 dark:bg-slate-500/20', label: 'COPY' };
      
      // Document events
      case 'document_upload':
        return { icon: Activity, color: 'text-teal-500 dark:text-teal-400', bg: 'bg-teal-500/10 dark:bg-teal-500/20', label: 'UPLOAD' };
      case 'document_view':
        return { icon: Activity, color: 'text-sky-500 dark:text-sky-400', bg: 'bg-sky-500/10 dark:bg-sky-500/20', label: 'VIEW' };
      
      // User activity events
      case 'login':
        return { icon: Users, color: 'text-green-500 dark:text-green-400', bg: 'bg-green-500/10 dark:bg-green-500/20', label: 'LOGIN' };
      case 'logout':
        return { icon: Users, color: 'text-slate-500 dark:text-slate-400', bg: 'bg-slate-500/10 dark:bg-slate-500/20', label: 'LOGOUT' };
      
      // Gamification events
      case 'badge_earned':
        return { icon: TrendingUp, color: 'text-amber-500 dark:text-amber-400', bg: 'bg-amber-500/10 dark:bg-amber-500/20', label: 'BADGE' };
      case 'streak_achieved':
        return { icon: Zap, color: 'text-orange-500 dark:text-orange-400', bg: 'bg-orange-500/10 dark:bg-orange-500/20', label: 'STREAK' };
      case 'leaderboard_rank_up':
        return { icon: TrendingUp, color: 'text-purple-500 dark:text-purple-400', bg: 'bg-purple-500/10 dark:bg-purple-500/20', label: 'RANK' };
      
      // Admin events
      case 'user_invited':
        return { icon: Users, color: 'text-blue-500 dark:text-blue-400', bg: 'bg-blue-500/10 dark:bg-blue-500/20', label: 'INVITE' };
      case 'group_created':
        return { icon: Users, color: 'text-violet-500 dark:text-violet-400', bg: 'bg-violet-500/10 dark:bg-violet-500/20', label: 'GROUP' };
      
      // Content gaps
      case 'content_gap':
        return { icon: AlertTriangle, color: 'text-amber-500 dark:text-amber-400', bg: 'bg-amber-500/10 dark:bg-amber-500/20', label: 'GAP' };
      case 'performance_update':
        return { icon: TrendingUp, color: 'text-emerald-500 dark:text-emerald-400', bg: 'bg-emerald-500/10 dark:bg-emerald-500/20', label: 'PERF' };
      default:
        return { icon: Activity, color: 'text-slate-500 dark:text-slate-400', bg: 'bg-slate-500/10 dark:bg-slate-500/20', label: 'EVENT' };
    }
  };
  
  const getEventMessage = (event) => formatEventMessage(event);

  return (
    <div className={`relative overflow-hidden rounded-2xl ${className}`} data-testid="live-broadcast-hero">
      {/* Theme-aware background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-100 via-slate-50 to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl" />
      
      {/* Subtle grid pattern overlay */}
      <div 
        className="absolute inset-0 opacity-30 dark:opacity-10"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(148, 163, 184, 0.4) 1px, transparent 0)`,
          backgroundSize: '24px 24px'
        }}
      />
      
      {/* Content */}
      <div className="relative z-10 p-6 md:p-8">
        {/* Header Row */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            {/* Live Indicator */}
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${broadcastStatus.className}`}>
              <span className="relative flex h-2.5 w-2.5">
                {broadcastStatus.pulse && (
                  <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${broadcastStatus.dotClassName} opacity-75`}></span>
                )}
                <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${broadcastStatus.dotClassName}`}></span>
              </span>
              <span className="text-xs font-bold uppercase tracking-widest font-mono">
                {broadcastStatus.label}
              </span>
            </div>
            
            <h2 className="text-xl md:text-2xl font-bold text-foreground tracking-tight">
              People Activity Broadcast
            </h2>
          </div>
          
          {/* Controls */}
          <div className="flex items-center gap-3">
            {/* Stream Toggle */}
            <button
              onClick={toggleEnabled}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                streamEnabled
                  ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                  : 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20'
              }`}
              title={streamEnabled ? 'Stream enabled - Click to disable' : 'Stream disabled - Click to enable'}
              data-testid="stream-toggle-btn"
            >
              <Power className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{streamEnabled ? 'On' : 'Off'}</span>
            </button>

            {/* Sound Toggle */}
            <button
              onClick={toggleSound}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                soundEnabled 
                  ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20' 
                  : 'bg-muted text-muted-foreground border border-border'
              }`}
              title={soundEnabled ? 'Sound alerts enabled' : 'Sound alerts disabled'}
              data-testid="sound-toggle-btn"
            >
              {soundEnabled ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{soundEnabled ? 'Sound On' : 'Sound Off'}</span>
            </button>
          </div>
        </div>
        
        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <StatCard 
            icon={MessageSquare} 
            value={stats.retrievalsToday || 0} 
            label="Answers" 
            color="blue"
          />
          <StatCard 
            icon={ThumbsUp} 
            value={stats.feedbackToday || 0} 
            label="Feedback" 
            color="purple"
          />
          <StatCard 
            icon={AlertTriangle} 
            value={stats.contentGapsToday || 0} 
            label="Gaps Found" 
            color="amber"
          />
        </div>
        
        {/* Ticker Container */}
        <div className="relative rounded-xl overflow-hidden bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm border border-slate-200 dark:border-slate-700/50">
          <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-800/30">
            <Radio className={`w-4 h-4 ${hasEvents ? 'text-red-500 dark:text-red-400 animate-pulse' : 'text-muted-foreground'}`} />
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
              Real-time Events
            </span>
          </div>
          
          {hasEvents && streamEnabled ? (
            <Marquee 
              speed={50} 
              gradient={true} 
              gradientColor="var(--marquee-gradient, #f8fafc)"
              gradientWidth={50}
              pauseOnHover={true}
              onCycleComplete={flushPendingEvents}
              className="min-h-[72px] py-3 [--marquee-gradient:#f8fafc] dark:[--marquee-gradient:#0f172a]"
              data-testid="live-broadcast-ticker"
            >
              {displayEvents.map((event, idx) => {
                const config = getEventConfig(event);
                const Icon = config.icon;
                
                  return (
                    <div 
                      key={`${event.timestamp}-${idx}`}
                      className={`flex items-center gap-3 mx-4 px-4 py-2 rounded-lg bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/50 shadow-sm ${
                        freshEventKeys.includes(getEventId(event)) ? 'ticker-fade-in' : ''
                      }`}
                      data-testid={`broadcast-event-${event.type}`}
                    >
                    <div className={`p-1.5 rounded-md ${config.bg}`}>
                      <Icon className={`w-4 h-4 ${config.color}`} />
                    </div>
                    <span className="font-mono text-sm text-foreground whitespace-nowrap">
                      {getEventMessage(event)}
                    </span>
                    <span className="font-mono text-xs text-muted-foreground whitespace-nowrap">
                      {formatRelativeFreshness(event.timestamp)} • {formatTime(event.timestamp)}
                    </span>
                  </div>
                );
              })}
            </Marquee>
          ) : !streamEnabled ? (
            <div className="relative flex min-h-[72px] items-center overflow-hidden px-4 py-4">
              <div className="relative mx-auto flex max-w-xl flex-col items-center gap-2 text-center">
                <div className="flex items-center gap-2 rounded-full border border-border bg-muted px-3 py-1.5">
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-muted-foreground" />
                  <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                    Paused
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Activity broadcast is turned off.
                </p>
              </div>
            </div>
          ) : (
            <div className="relative flex min-h-[72px] items-center justify-center overflow-hidden px-4 py-4">
              <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-primary/5 to-transparent" />
              <div className="relative flex items-center gap-3 text-center">
                <span className="relative flex h-2.5 w-2.5 shrink-0">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-primary/80" />
                </span>
                <p className="text-sm text-muted-foreground">
                  Activity will appear here as employees interact with Eva.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Stat Card Component for the Broadcast Hero
 */
function StatCard({ icon: Icon, value, label, color }) {
  const accentClasses = {
    blue: 'text-blue-600 bg-blue-500/10 dark:text-blue-400 dark:bg-blue-500/15',
    purple: 'text-purple-600 bg-purple-500/10 dark:text-purple-400 dark:bg-purple-500/15',
    amber: 'text-amber-600 bg-amber-500/10 dark:text-amber-400 dark:bg-amber-500/15',
    emerald: 'text-emerald-600 bg-emerald-500/10 dark:text-emerald-400 dark:bg-emerald-500/15',
  };
  
  const accentClass = accentClasses[color] || accentClasses.blue;
  
  return (
    <div className="rounded-xl border border-slate-200 bg-white/65 p-4 text-foreground backdrop-blur-sm dark:border-slate-700/50 dark:bg-slate-800/45">
      <div className="flex items-center gap-3">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${accentClass}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <p className="text-2xl font-bold font-mono">{value}</p>
          <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
        </div>
      </div>
    </div>
  );
}

// Icon-only lookup for the Eva People ticker. Text content always comes
// from the shared formatEventMessage/formatEventContext contract in
// EventContract.js, which for retrieval/query-type events only ever surfaces
// a topic/sub_topic/category label plus country/department -- never raw
// free-text -- so this map intentionally carries no copy, only emoji.
const TICKER_EVENT_ICONS = {
  // Backend emits type "query" for retrieval-style events (see
  // broadcast_retrieval_event in backend/routers/websocket.py); "retrieval"
  // is kept here too since EventContract.js treats the two as synonyms.
  query: '📄',
  retrieval: '📄',
  feedback: (data) => (data?.feedback === 'positive' ? '👍' : '💭'),
  content_gap: '🔍',
};

const getTickerIcon = (event) => {
  const entry = TICKER_EVENT_ICONS[event?.type];
  if (typeof entry === 'function') return entry(event?.data);
  return entry || '⚡';
};

/**
 * Eva People Activity Ticker
 * A subtle, non-intrusive ticker for the chat interface showing organization activity
 */
export function EvaPeopleActivityTicker({ className = '' }) {
  const { events, isConnected } = useAnalyticsStream();
  const [isVisible, setIsVisible] = useState(true);

  // Event copy is always derived from the shared formatEventMessage contract
  // (never raw event.data.query / event.data.query_pattern) so this ticker
  // structurally cannot leak free-text query content, matching every sibling
  // ticker in this file and RealTimeActivityFeed.
  const displayEvents = useMemo(() => {
    return events.slice(-15).map(event => ({
      icon: getTickerIcon(event),
      text: formatEventMessage(event),
    }));
  }, [events]);

  const hasEvents = displayEvents.length > 0;

  useEffect(() => {
    if (sessionStorage.getItem('eva_ticker_dismissed') === 'true') {
      setIsVisible(false);
    }
  }, []);

  const handleDismiss = () => {
    sessionStorage.setItem('eva_ticker_dismissed', 'true');
    setIsVisible(false);
  };
  
  if (!isVisible) return null;
  
  return (
    <div 
      className={`relative bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 border-b border-slate-200 dark:border-slate-700/50 ${className}`}
      data-testid="eva-people-ticker"
    >
      <div className="flex items-center h-8">
        {/* Live badge */}
        <div className="flex items-center gap-1.5 px-3 border-r border-slate-200 dark:border-slate-700/50 shrink-0">
          <span className="relative flex h-1.5 w-1.5">
            {isConnected && (
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            )}
            <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${isConnected ? 'bg-red-500' : 'bg-slate-400 dark:bg-slate-500'}`}></span>
          </span>
          <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground font-mono">
            {isConnected ? 'Live' : 'Feed'}
          </span>
        </div>
        
        {/* Ticker */}
        <div className="flex-1 overflow-hidden min-w-0">
          {hasEvents ? (
            <Marquee
              speed={40}
              gradient={true}
              gradientColor="var(--marquee-gradient, #f8fafc)"
              gradientWidth={30}
              pauseOnHover={true}
              className="[--marquee-gradient:#f8fafc] dark:[--marquee-gradient:#0f172a]"
            >
              {displayEvents.map((event, idx) => (
                <span
                  key={idx}
                  className="mx-6 text-xs font-mono text-muted-foreground whitespace-nowrap"
                >
                  {event.icon} {event.text}
                </span>
              ))}
            </Marquee>
          ) : (
            <div className="flex h-full items-center justify-center">
              <div className="flex items-center gap-2 text-xs text-muted-foreground font-mono">
                <Clock className="w-3 h-3 animate-pulse" />
                <span>Waiting for activity...</span>
              </div>
            </div>
          )}
        </div>
        
        {/* Close button */}
        <button 
          onClick={handleDismiss}
          className="px-3 text-muted-foreground hover:text-foreground transition-colors shrink-0"
          aria-label="Hide activity ticker"
          data-testid="ticker-close-btn"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default LiveBroadcastHero;
